/**
 * @module CoreLayers
 */
import { Vector as VectorLayer, Group as LayerGroup } from 'ol/layer';
import VectorTileLayer from 'ol/layer/VectorTile.js';
import { Vector as VectorSource } from 'ol/source';
import Graticule from 'ol/layer/Graticule.js';
import { Attribution, Zoom } from 'ol/control';
import { Stroke } from 'ol/style';
import { apply, applyStyle } from 'ol-mapbox-style';
import { t } from '../utils/i18n.js';

/**
 * @classdesc
 * This class encapsulates the core layers of the map. These are the layers
 * that are always present on the map, regardless of the configuration.
 */

class CoreLayers {
  /**
   * Create the core layers
   */
  constructor(language) {
    this.language = language;
    this.graticule = this.createGraticule();
    this.baseLayerGroup = this.createBaseLayerGroup();
    this.borderLayer = this.createBorderLayer();
    this.labelLayer = this.createLabelLayer();
    this.zoomControl = this.createZoomControl();
    this.zoomControl.set('name', 'nhcCoreZoom');
    this.attributionControl = this.createAttributionControl();
    this.attributionControl.set('name', 'nhcCoreAttribution');
  }

  /**
   * Define the graticule layer
   * @returns {Graticule}
   */
  createGraticule() {
    // Define the suffixes for different languages
    const suffixes = {
      en: { N: 'N', S: 'S', E: 'E', W: 'W' },
      es: { N: 'N', S: 'S', E: 'E', W: 'O' }, // "Oeste" for West
    };

    // Get the suffixes for the current language, or default to English
    const langSuffixes = suffixes[this.language] || suffixes.en;

    const nhcGraticule = new Graticule({
      strokeStyle: new Stroke({
        color: "rgba(0, 0, 0, 0.2)",
        width: 1,
        lineDash: [0.5, 4],
      }),
      targetSize: 60,
      showLabels: true,
      wrapX: true,
      lonLabelPosition: 0.005,
      latLabelPosition: 0.04,
      zIndex: 98,
      // Add custom formatters for longitude and latitude labels
      lonLabelFormatter: (lon) => {
        const formattedLon = Math.abs(Math.round(lon));
        const suffix = lon < 0 ? langSuffixes.W : langSuffixes.E;
        return `${formattedLon}°${suffix}`;
      },
      latLabelFormatter: (lat) => {
        const formattedLat = Math.abs(Math.round(lat));
        const suffix = lat < 0 ? langSuffixes.S : langSuffixes.N;
        return `${formattedLat}°${suffix}`;
      },
    });
    nhcGraticule.set('name', 'nhcGraticule');
    return nhcGraticule;
  }

  /**
   * Create the base layer group with no labels.
   * @returns {LayerGroup}
   */
  createBaseLayerGroup() {
    const attributions = "Sources: Esri, TomTom, Garmin, FAO, NOAA, USGS, " +
      "© OpenStreetMap contributors, and the GIS User Community";
    const attrLayer = new VectorLayer({
      source: new VectorSource({
        attributions: attributions,
      }),
    });
    const nhcMapLayer = new LayerGroup({
      layers: [attrLayer],
      declutter: true,
      zIndex: 0,
    });
    const glStyle = "/arcgis-map-styles/NHC_basemap_noLabels.json";
    apply(nhcMapLayer, glStyle);
    nhcMapLayer.set('name', 'nhcMapLayer');
    return nhcMapLayer;
  }

  /**
   * Create the small label layer.
   * @returns {VectorTileLayer}
   */
  createLabelLayer() {
    const nhcLabelLayer = new VectorTileLayer({
      declutter: true,
      zIndex: 100,
      visible: true,
    });
    let glStyle = "";
    if (this.language === "en") {
      glStyle = "/arcgis-map-styles/NHC_basemap_smLabels.json";
    }
    else {
      glStyle = `/arcgis-map-styles/NHC_basemap_smLabels_${this.language}.json`;
    }
    applyStyle(nhcLabelLayer, glStyle);
    nhcLabelLayer.set('name', 'nhcLabelLayer');
    return nhcLabelLayer;
  }
  
  /**
   * Create the small label layer.
   * @returns {VectorTileLayer}
   */
  createBorderLayer() {
    const nhcBorderLayer = new VectorTileLayer({
      declutter: true,
      zIndex: 50,
      visible: true,
    });
    const glStyle = "/arcgis-map-styles/NHC_basemap_borders.json";
    applyStyle(nhcBorderLayer, glStyle);
    nhcBorderLayer.set('name', 'nhcBorderLayer');
    return nhcBorderLayer;
  }

  createAttributionControl() {
    return new Attribution({
      collapsible: true,
      tipLabel: t('attribution_tip'),
    });
  }

  createZoomControl() {
    return new Zoom({
      zoomInTipLabel: t('zoom_in_tip'),
      zoomOutTipLabel: t('zoom_out_tip'),
    });
  }

}

export default CoreLayers;